/**
 * The control scale. Three steps, and nothing between them.
 *
 * **Why a scale at all.** Every form in the platform picked its own submit
 * button height by hand and none of them matched the inputs above it. The
 * Client Portal's sign-in button stood at 36px under 56px fields — a fifth of
 * the field's height missing, directly below it, full width, so the mismatch
 * was the most visible edge on the screen. The Government Portal reached 47px
 * via `py: 1.4`, the admin portal 48px via `size="large"` plus `py: 1.25`.
 * Three forms, three numbers, none of them the field height, and every one of
 * them arrived at by nudging padding until it looked close enough.
 *
 * Padding is the wrong control for this. A height is what was wanted, so a
 * height is what these name. Each step is bound to MUI's button sizes in the
 * theme (`makeComponents`), which is what makes `size="large"` mean "stands
 * with a text field" rather than "a bit bigger than default".
 */
export const controlHeight = {
  /** Dense row controls — a table's action button, a filter. */
  sm: 32,
  /** The default, and every control in an app topbar. */
  md: 36,
  /**
   * Full-height form controls: text fields, selects, and the submit button
   * that sits with them. Matches MUI's outlined-input height, which is what
   * the fields already stand at — this is the number they were always going
   * to have to agree with.
   */
  lg: 56,
} as const;

export type ControlHeight = keyof typeof controlHeight;

/**
 * The smallest hit area a touch control may have, in px (CSS px on the web,
 * dp on mobile).
 *
 * 44 is Apple's HIG floor and WCAG 2.5.5's enhanced target. A control can draw
 * smaller than this — a 16px bin icon in a dense row — so long as the area
 * that answers a finger reaches it: an invisible pseudo-element on the web,
 * `hitSlop` on React Native.
 */
export const touchTarget = 44;

/**
 * The floor for a mouse-only target (WCAG 2.2 SC 2.5.8, AA): 24px square.
 */
export const pointerTarget = 24;
